/**
 * GridItem Component
 * Wrapper component for individual chart items in the react-grid-layout
 * Provides drag handles, theme support, and consistent styling
 */

import React, { forwardRef } from 'react';
import { Box, Typography } from '@mui/material';

/**
 * GridItem - Wraps individual chart components with grid-specific styling and drag handles
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.id - Unique identifier for the grid item
 * @param {string} props.title - Title displayed in the item header
 * @param {React.ReactNode} props.children - Chart content to render
 * @param {string} props.mode - Theme mode ('light' or 'dark')
 * @param {Object} props.style - Additional inline styles
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onMouseDown - Mouse down event handler
 * @param {Function} props.onMouseUp - Mouse up event handler
 * @param {Function} props.onTouchEnd - Touch end event handler
 * @param {Object} props.rest - Additional props to spread
 * @param {React.Ref} ref - Forwarded ref for grid layout
 * @returns {React.ReactElement} Rendered grid item
 */
const GridItem = forwardRef(
    (
        {
            id,
            title,
            children,
            mode,
            style,
            className,
            onMouseDown,
            onMouseUp,
            onTouchEnd,
            ...props
        },
        ref
    ) => {
        const itemStyle = {
            border: `1px solid ${mode === 'light' ? '#e0e0e0' : '#555'}`,
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: mode === 'light' ? '#fff' : '#333',
            transition: 'background-color 0.3s, border-color 0.3s',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            ...style
        };

        const headerStyle = {
            padding: '8px 12px',
            borderBottom: `1px solid ${mode === 'light' ? '#e0e0e0' : '#555'}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: mode === 'light' ? '#f5f5f5' : '#2c2c2c',
            cursor: 'grab',
            userSelect: 'none'
        };

        const contentStyle = {
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        };

        const dragHandleStyle = {
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'grab',
            color: mode === 'light' ? '#666' : '#aaa',
            fontSize: '18px'
        };

        return (
            <div
                ref={ref}
                style={itemStyle}
                className={className}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onTouchEnd={onTouchEnd}
                {...props}
            >
                <Box sx={headerStyle}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 'bold',
                            color: mode === 'light' ? '#000' : '#fff',
                            flex: 1
                        }}
                    >
                        {title}
                    </Typography>
                    <div className="drag-handle" style={dragHandleStyle}>
                        ⋮⋮
                    </div>
                </Box>
                <Box sx={contentStyle}>
                    {children}
                </Box>
            </div>
        );
    }
);

GridItem.displayName = 'GridItem';

export default GridItem;
